/**
 * ReviewPage - Review all responses before locking
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Lock, Edit2, ChevronDown, ChevronUp, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/shared/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewPage = () => {
  const navigate = useNavigate();
  const [responses, setResponses] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locking, setLocking] = useState(false);
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  const userId = sessionStorage.getItem('user_id');
  const relationshipId = sessionStorage.getItem('relationship_id');
  const level = sessionStorage.getItem('selected_level') || 'CORE';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch questions and responses in parallel
      const [questionsRes, responsesRes] = await Promise.all([
        axios.get(`${API}/questions/${level}`),
        axios.get(`${API}/responses/${relationshipId}/${userId}`)
      ]);

      setQuestions(questionsRes.data);
      setResponses(responsesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load your responses');
      setLoading(false);
    }
  };

  const getResponseForQuestion = (questionId) => {
    const response = responses.find(r => r.question_id === questionId);
    return response?.response_json?.value || null;
  };

  const formatResponse = (value, responseType) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-stone-400 italic">No response</span>;
    }
    
    if (Array.isArray(value)) {
      return value.length > 0 
        ? value.join(', ') 
        : <span className="text-stone-400 italic">No selections</span>;
    }
    
    return value;
  };

  const handleEditQuestion = (questionIndex) => {
    // Navigate back to reflection with the specific question
    sessionStorage.setItem('resume_question_index', questionIndex.toString());
    navigate('/reflection');
  };

  const handleLock = async () => {
    setLocking(true);
    try {
      const response = await axios.post(`${API}/responses/lock?user_id=${userId}`, {
        relationship_id: relationshipId
      });

      toast.success('Responses locked successfully!');
      
      // Check if both locked
      if (response.data.both_locked) {
        navigate('/reveal');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error locking responses:', error);
      toast.error('Failed to lock responses');
      setLocking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center" style={{minHeight: 'calc(100vh - 100px)'}}>
          <Loader2 className="w-8 h-8 animate-spin text-stone-900" />
        </div>
      </div>
    );
  }

  const answeredCount = questions.filter(q => getResponseForQuestion(q.question_id)).length;
  const totalCount = questions.length;

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} />
      <div className="py-8 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-2">Review Your Responses</h2>
            <p className="text-stone-600">
              Please review all your answers before locking. Once locked, they cannot be edited.
            </p>
            <div className="mt-4 flex items-center gap-2">
              <div className="flex-1 bg-stone-200 rounded-full h-2">
                <div 
                  className="bg-mc-blue-deep h-2 rounded-full transition-all"
                  style={{ width: `${(answeredCount / totalCount) * 100}%` }}
                />
              </div>
              <span className="text-sm text-stone-600">{answeredCount}/{totalCount} answered</span>
            </div>
          </div>

          {/* Responses List */}
          <div className="space-y-4 mb-8">
            {questions.map((question, idx) => {
              const response = getResponseForQuestion(question.question_id);
              const isExpanded = expandedQuestion === idx;
              const hasResponse = response !== null && response !== undefined && response !== '';

              return (
                <div 
                  key={question.question_id}
                  className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
                    hasResponse ? 'border-stone-100' : 'border-amber-200'
                  }`}
                >
                  {/* Question Header */}
                  <button
                    onClick={() => setExpandedQuestion(isExpanded ? null : idx)}
                    className="w-full p-4 flex items-start justify-between text-left hover:bg-stone-50 transition-colors"
                  >
                    <div className="flex-1 min-w-0 pr-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-stone-500 uppercase tracking-wider">
                          {question.category}
                        </span>
                        {hasResponse ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Answered
                          </span>
                        ) : (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            Not answered
                          </span>
                        )}
                      </div>
                      <p className="text-stone-900 font-medium text-sm sm:text-base">
                        {question.prompt_text}
                      </p>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-stone-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-stone-400 flex-shrink-0" />
                    )}
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-stone-100">
                      <div className="pt-4">
                        <p className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
                          Your Response
                        </p>
                        <div className="bg-stone-50 rounded-lg p-4">
                          <p className="text-stone-700 whitespace-pre-wrap">
                            {formatResponse(response, question.response_type)}
                          </p>
                        </div>
                        
                        <button
                          onClick={() => handleEditQuestion(idx)}
                          className="mt-4 flex items-center gap-2 text-sm text-purple-600 hover:text-purple-800 font-medium"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit this response
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Warning Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <p className="text-sm text-amber-800">
              <strong>⚠️ Important:</strong> Once you lock your responses, they cannot be changed. 
              Your answers will remain private until your partner also locks their responses.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate('/reflection')}
              className="flex-1 px-6 py-4 border-2 border-stone-200 text-stone-700 rounded-full font-medium hover:bg-stone-50 transition-all flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Continue Editing
            </button>
            
            <button
              onClick={handleLock}
              disabled={locking || answeredCount < totalCount}
              className="flex-1 bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-6 py-4 font-medium transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="lock-responses"
            >
              {locking ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Locking...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  Lock My Responses ({answeredCount}/{totalCount})
                </>
              )}
            </button>
          </div>

          {answeredCount < totalCount && (
            <p className="text-center text-sm text-amber-600 mt-4">
              Please answer all questions before locking your responses.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewPage;
